import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../api";
import toast from "react-hot-toast";

import { setToken } from "../utils/tokenStorage";
import Logo from "../components/Logo";
import { Mail, KeyRound, ArrowRight, ArrowLeft } from "lucide-react";

const PARENT_URL = "https://getkhojo.com";

export default function LoginSip() {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const sendCode = async () => {
    if (!email.trim()) { toast.error("Enter email"); return; }
    setBusy(true);
    try {
      await api.post("/api/auth/login", { email: email.trim() });
      toast.success("Verification code sent");
      setStep(2);
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : "Failed");
    } finally { setBusy(false); }
  };

  const verify = async () => {
    if (!code.trim()) { toast.error("Enter code"); return; }
    setBusy(true);
    try {
      const res = await api.post("/api/auth/verify", {
        email: email.trim(),
        code: code.trim(),
        purpose: "login"
      });
      setToken("aegis_token", res.data.access_token);
      toast.success("Welcome to SIP Tester");
      setTimeout(() => nav("/dashboard"), 500);
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : "Invalid code");
    } finally { setBusy(false); }
  };

  const labelStyle = {
    display: "block", fontSize: 12, fontWeight: 600, textTransform: "uppercase",
    letterSpacing: "0.08em", color: "var(--au-text-2)", marginBottom: 8
  };

  return (
    <div className="au-root" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", padding: 20, position: "relative" }}>
      <a className="au-pill au-pill-glass au-pill-sm" href={PARENT_URL} style={{ position: "absolute", top: 24, left: 24, zIndex: 10 }}>
        <ArrowLeft size={16} /> Back
      </a>

      <div className="au-glass au-pad-lg" style={{ maxWidth: 440, width: "100%", position: "relative", zIndex: 5 }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", marginBottom: 28 }}>
          <Logo size="large" />
          <div style={{ marginTop: 12, fontSize: 12, fontWeight: 600, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--au-muted)" }}>
            Test · Analyze · Resolve
          </div>
        </div>

        <h2 style={{ fontSize: 26, fontWeight: 700, margin: 0, marginBottom: 6, textAlign: "center", color: "var(--au-text)" }}>
          {step === 1 ? "Welcome back" : "Verify it's you"}
        </h2>
        <p style={{ color: "var(--au-muted)", fontSize: 14, margin: 0, marginBottom: 28, textAlign: "center" }}>
          {step === 1 ? "Login with your email to continue" : "We sent a code to your inbox"}
        </p>

        {step === 1 ? (
          <>
            <label style={labelStyle}>Email address</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={e => e.key === "Enter" && !busy && sendCode()}
              placeholder="you@company.com"
              autoFocus
              className="au-input"
              style={{ marginBottom: 20 }}
            />
            <button
              onClick={sendCode}
              disabled={busy}
              className="au-pill au-pill-primary"
              style={{ width: "100%" }}>
              <Mail size={18} />
              {busy ? "Sending..." : "Send verification code"}
            </button>
          </>
        ) : (
          <>
            <div style={{ padding: "14px 16px", background: "var(--au-glass-strong)", border: "1px solid var(--au-hair)", borderRadius: "var(--au-r-input)", marginBottom: 24, fontSize: 13, color: "var(--au-text-2)" }}>
              Code sent to <strong style={{ color: "var(--au-accent-ink)" }}>{email}</strong>
            </div>
            <label style={labelStyle}>6-digit code</label>
            <input
              type="text"
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              onKeyDown={e => e.key === "Enter" && !busy && verify()}
              placeholder="000000"
              autoFocus
              maxLength={6}
              className="au-input"
              style={{ marginBottom: 20, fontFamily: "var(--au-font-mono)", fontSize: 24, textAlign: "center", letterSpacing: 12, fontWeight: 700 }}
            />
            <button
              onClick={verify}
              disabled={busy}
              className="au-pill au-pill-primary"
              style={{ width: "100%" }}>
              <KeyRound size={18} />
              {busy ? "Verifying..." : "Verify & continue"}
              <ArrowRight size={18} />
            </button>
            <button
              onClick={() => { setStep(1); setCode(""); }}
              className="au-pill au-pill-ghost"
              style={{ width: "100%", marginTop: 12 }}>
              Use different email
            </button>
          </>
        )}

        <div style={{ marginTop: 28, paddingTop: 22, borderTop: "1px solid var(--au-hair)", fontSize: 14, color: "var(--au-muted)", textAlign: "center" }}>
          New to SIP Tester?{" "}
          <Link to="/register" style={{ color: "var(--au-accent-ink)", textDecoration: "none", fontWeight: 600 }}>
            Create account
          </Link>
        </div>
      </div>
    </div>
  );
}
